import {
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import { tokens } from "../../../../theme";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RoleOption {
  id: number;
  name: string;
  description: string;
}

interface VenueCrewRecommendation {
  crew_member_id: number;
  user_id: number;
  full_name: string;
  email: string | null;
  profile_picture: string | null;
  roles: string[];
  total_streams_attended: number;
  last_attended_at: string | null;
  reason: string;
}

interface VenueCrewRecommendationResponse {
  venue: {
    id: number;
    name: string;
  };
  recommendations: VenueCrewRecommendation[];
}

/** Minimal shoot context shown in the modal subtitle. */
interface ShootSummary {
  productionTitle: string;
  location: string;
  startTime: string; // e.g. "12:47"
  endTime: string; // e.g. "14:47"
}

interface CrewVenueModalProps {
  open: boolean;
  onClose: () => void;
  /** The shoot/stream this crew member is being assigned to. */
  streamId: number;
  /** The venue drives this endpoint, because recommendations are based on past streams at this venue. */
  venueId: number;
  availableRoles: RoleOption[];
  assignedCrewIds: number[];
  /** Context shown under the modal title. */
  shoot: ShootSummary;
  /** Called after a crew member is assigned, so the parent can refetch its callsheet. */
  onAssigned?: () => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MAX_CANDIDATES_SHOWN = 3; // Keep this a fast decision tool — top 3 only.

const CrewVenueModal: React.FC<CrewVenueModalProps> = ({
  open,
  onClose,
  streamId,
  venueId,
  availableRoles,
  assignedCrewIds,
  shoot,
  onAssigned,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [venueName, setVenueName] = useState("");
  const [recommendations, setRecommendations] = useState<
    VenueCrewRecommendation[]
  >([]);
  const [selectedRoleByCrew, setSelectedRoleByCrew] = useState<
    Record<number, number>
  >({});
  const [failedProfileImages, setFailedProfileImages] = useState<
    Record<number, boolean>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<number | null>(null);

  const roleOptionsForRecommendation = useCallback(
    (recommendation: VenueCrewRecommendation) => {
      const normalizeRole = (roleName: string) => roleName.trim().toLowerCase();
      const crewRoleNames = new Set(recommendation.roles.map(normalizeRole));

      // The recommendation endpoint returns role names, while the callsheet create
      // endpoint needs role ids. Matching by real role names bridges those APIs
      // without inventing a role or sending a fake id.
      return availableRoles.filter((role) =>
        crewRoleNames.has(normalizeRole(role.name)),
      );
    },
    [availableRoles],
  );

  const initialsForName = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "NA";

  const formatLastAttended = (value: string | null) => {
    if (!value) return "No date recorded";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // This is the frontend integration point for the DRF @action:
  // @action(detail=True, methods=['get'], url_path='venue-crew-recommendation')
  // Because it is detail=True, the venue id must be in the URL.
  const fetchCandidates = useCallback(async () => {
    if (!venueId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get<VenueCrewRecommendationResponse>(
        `/api/venues/venue/${venueId}/venue-crew-recommendation/`,
      );

      const nextRecommendations = (
        response.data.recommendations || []
      ).slice(0, MAX_CANDIDATES_SHOWN);

      setVenueName(response.data.venue?.name || "");
      setRecommendations(nextRecommendations);

      setSelectedRoleByCrew(
        nextRecommendations.reduce<Record<number, number>>(
          (selectedRoles, recommendation) => {
            const firstMatchingRole =
              roleOptionsForRecommendation(recommendation)[0];

            if (firstMatchingRole) {
              selectedRoles[recommendation.crew_member_id] =
                firstMatchingRole.id;
            }

            return selectedRoles;
          },
          {},
        ),
      );
    } catch (err) {
      console.error(err);
      setError("Failed to load venue crew recommendations.");
    } finally {
      setLoading(false);
    }
  }, [roleOptionsForRecommendation, venueId]);

  // Re-fetch every time the modal opens, so a page refresh or venue change uses
  // current backend recommendations instead of stale UI state.
  useEffect(() => {
    if (open) {
      setFailedProfileImages({});
      fetchCandidates();
    }
  }, [open, fetchCandidates]);

  const handleAssign = async (recommendation: VenueCrewRecommendation) => {
    const selectedRoleId = selectedRoleByCrew[recommendation.crew_member_id];
    const selectedRole = availableRoles.find(
      (role) => role.id === selectedRoleId,
    );

    if (!selectedRole) {
      toast.error("Select a matching role before adding this crew member.");
      return;
    }

    try {
      setAssigningId(recommendation.crew_member_id);

      // The recommendation endpoint only tells us who is good for this venue.
      // The callsheet endpoint is still the source of truth for actually adding
      // that crew member to this specific stream/callsheet.
      await apiService.post("/api/call-sheets/callsheet/", {
        stream_id: streamId,
        crew_member_id: recommendation.crew_member_id,
        roles_ids: [selectedRole.id],
        assignment: [
          {
            role: selectedRole.name,
            notes: recommendation.reason,
          },
        ],
      });

      toast.success(
        `${recommendation.full_name || "Crew member"} added as ${selectedRole.name}.`,
      );
      await onAssigned?.();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add recommended crew member.");
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px", overflow: "hidden" },
      }}
    >
      {/* ----------------------------------------------------------------- */}
      {/* Header: role being filled + shoot context + close button          */}
      {/* ----------------------------------------------------------------- */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: `1px solid ${colors.primary[700]}`,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: colors.primary[100] }}
          >
            Recommended Crew
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.primary[300], mt: 0.5 }}
          >
            {shoot.productionTitle} · {venueName || shoot.location} ·{" "}
            {shoot.startTime} - {shoot.endTime}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* The cards below render only what the backend recommendation endpoint
          returns. No ratings, distance, gear, or prices are invented here. */}
      <DialogContent
        sx={{ backgroundColor: colors.primary[900], p: 3 }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : recommendations.length === 0 ? (
          <Alert severity="info">
            No venue-based crew recommendations were found for this callsheet.
          </Alert>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {recommendations.map((recommendation, index) => {
              const matchingRoleOptions =
                roleOptionsForRecommendation(recommendation);
              const isAlreadyAssigned = assignedCrewIds.includes(
                recommendation.crew_member_id,
              );
              const selectedRoleId =
                selectedRoleByCrew[recommendation.crew_member_id] || "";
              const showProfilePicture =
                Boolean(recommendation.profile_picture) &&
                !failedProfileImages[recommendation.crew_member_id];
              const profilePictureUrl = showProfilePicture
                ? recommendation.profile_picture ?? undefined
                : undefined;

              return (
              <Paper
                key={recommendation.crew_member_id}
                elevation={0}
                sx={{
                  position: "relative",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "16px",
                  border: index === 0
                    ? `2px solid ${colors.primary[400]}`
                    : `1px solid ${colors.primary[700]}`,
                  backgroundColor: colors.primary[800],
                }}
              >
                {index === 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: 16,
                      backgroundColor: colors.primary[400],
                      color: colors.primary[900],
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "999px",
                    }}
                  >
                    MOST EXPERIENCED HERE
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                    mt: index === 0 ? 1 : 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      overflow: "hidden",
                      backgroundColor: "#f4ebe6",
                      color: colors.primary[500],
                      border: `1px solid ${colors.primary[600]}`,
                      flexShrink: 0,
                    }}
                  >
                    {showProfilePicture ? (
                      <Box
                        component="img"
                        src={profilePictureUrl}
                        alt={recommendation.full_name || "Crew member"}
                        onError={() =>
                          setFailedProfileImages((current) => ({
                            ...current,
                            [recommendation.crew_member_id]: true,
                          }))
                        }
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      initialsForName(recommendation.full_name)
                    )}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: colors.primary[100] }}>
                      {recommendation.full_name || "Unnamed crew member"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.primary[300] }}
                    >
                      {recommendation.email || "No email recorded"}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  {recommendation.roles.length > 0 ? (
                    recommendation.roles.map((roleName) => (
                      <Chip
                        key={roleName}
                        label={roleName}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: "4px", fontSize: "11px" }}
                      />
                    ))
                  ) : (
                    <Typography variant="caption" sx={{ color: colors.primary[300] }}>
                      No crew roles recorded
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.primary[400], letterSpacing: "0.05em" }}
                  >
                    VENUE EXPERIENCE
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.primary[100] }}>
                    {recommendation.reason}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.primary[300] }}>
                    Last attended: {formatLastAttended(recommendation.last_attended_at)}
                  </Typography>
                </Box>

                <FormControl
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  disabled={matchingRoleOptions.length === 0 || isAlreadyAssigned}
                >
                  <InputLabel id={`recommended-role-${recommendation.crew_member_id}`}>
                    Callsheet role
                  </InputLabel>
                  <Select
                    labelId={`recommended-role-${recommendation.crew_member_id}`}
                    label="Callsheet role"
                    value={selectedRoleId}
                    onChange={(event) =>
                      setSelectedRoleByCrew((current) => ({
                        ...current,
                        [recommendation.crew_member_id]: Number(event.target.value),
                      }))
                    }
                  >
                    {matchingRoleOptions.map((roleOption) => (
                      <MenuItem key={roleOption.id} value={roleOption.id}>
                        {roleOption.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  sx={{
                    mt: "auto",
                    pt: 2,
                    borderTop: `1px solid ${colors.primary[700]}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="caption" sx={{ color: colors.primary[300] }}>
                    {isAlreadyAssigned
                      ? "Already on callsheet"
                      : matchingRoleOptions.length === 0
                        ? "No matching role id"
                        : `${recommendation.total_streams_attended} past stream${
                            recommendation.total_streams_attended === 1
                              ? ""
                              : "s"
                          }`}
                  </Typography>
                  <Button
                    variant={index === 0 ? "contained" : "outlined"}
                    size="small"
                    disabled={
                      assigningId !== null ||
                      isAlreadyAssigned ||
                      matchingRoleOptions.length === 0 ||
                      !selectedRoleId
                    }
                    onClick={() => handleAssign(recommendation)}
                  >
                    {assigningId === recommendation.crew_member_id ? (
                      <CircularProgress size={16} />
                    ) : (
                      "Add"
                    )}
                  </Button>
                </Box>
              </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* ----------------------------------------------------------------- */}
      {/* Footer                                                             */}
      {/* ----------------------------------------------------------------- */}
      <Divider />
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" sx={{ color: colors.primary[400] }}>
          Ranked by completed streams at this venue
        </Typography>
      </Box>
    </Dialog>
  );
};

export default CrewVenueModal;
